import { createBrowserHistory } from 'history'
import { USE_NEW_API, getProgramFull } from '../../utils/contentApi'

const history = createBrowserHistory()

class ContentEngine {
    program = null
    subIndex = 0
    screenIndex = 0

    currentScreenData = null

    listeners = []

    /** 'left' when going forward (next), 'right' when going backward (prev) */
    slideDirection = null

    /**
     * When true, all content is fetched from the admin STAGING endpoints
     * (unpublished work) instead of the live ones. Set by the runner before
     * loadProgram() so admins can preview drafts without publishing to live.
     */
    staging = false

    /**
     * Callback fired when the last screen of the current subprogram is
     * completed (i.e. next() is called on the final screen).
     * Set by SubprogramRunner before loading.
     */
    onSubprogramComplete = null

    /**
     * Callback fired when prev() is called on the first screen (no earlier
     * screen exists). SubprogramRunner sets this to intercept the exit so it
     * can check for unsaved progress before navigating away.
     * Falls back to history.back() when not set.
     */
    onExitRequest = null

    /**
     * Set to true by handleStartFresh before calling notify().
     * ProgramTimelineNav checks this flag inside its subscribe callback and
     * clears completedSteps when it sees it, then immediately resets it to
     * false. Using the existing notify/subscribe bus is more reliable than a
     * separate callback because the bus is guaranteed to fire synchronously
     * before any React state batching cuts in.
     */
    _pendingProgressReset = false

    /**
     * Direct step-completion signal bypassing the React prop chain.
     * Screen components call tryEarnStep(globalStep) when a step is
     * earned sequentially. ProgramTimelineNav subscribes here so the green
     * highlight is applied synchronously without any render-cycle timing risk.
     */
    onStepCompleted = null

    globalHwm = 0             // highest globally-earned step number (across all screens)
    globalLastNext = 0        // which global step number the user last pressed Next FROM
    globalHwmJustEarned = false  // true only when the most recent tryEarnStep actually earned

    signalStepComplete(globalStep) {
        if (typeof this.onStepCompleted === 'function') {
            this.onStepCompleted(globalStep)
        }
    }

    // Returns the cumulative step offset for the current screen.
    getCurrentScreenOffset() {
        const screens = this.getAllScreenSteps()
        const current = screens.find(s => s.screenIndex === this.screenIndex)
        return current ? current.offset : 0
    }

    // Called on every Next button press with the global step number being left.
    // Returns true if the step was earned.
    //
    // A step earns when it is exactly one ahead of globalHwm AND the user just
    // pressed Next from globalHwm (globalLastNext === globalHwm). globalLastNext
    // is always kept equal to the step most recently departed via Next, so the
    // chain rebuilds naturally: pressing Next at the boundary step after any
    // navigation (circle-click or sequential) sets globalLastNext = globalHwm,
    // and the subsequent press earns globalHwm + 1.
    tryEarnStep(globalStep) {
        const isSequential =
            globalStep === this.globalHwm + 1 &&
            this.globalLastNext === this.globalHwm

        this.globalLastNext = globalStep

        if (isSequential) {
            this.globalHwm = globalStep
            this.globalHwmJustEarned = true
            this.signalStepComplete(globalStep)
        } else {
            this.globalHwmJustEarned = false
        }
        return isSequential
    }

    // Called by auto-advance (action-based completion). Updates both globalHwm and
    // globalLastNext when earning so the chain continues for the next step.
    tryEarnStepAutoAdvance(globalStep) {
        const isSequential =
            globalStep === this.globalHwm + 1 &&
            this.globalLastNext === this.globalHwm
        if (isSequential) {
            this.globalHwm = globalStep
            this.globalLastNext = globalStep
            this.globalHwmJustEarned = true
            this.signalStepComplete(globalStep)
        } else {
            this.globalHwmJustEarned = false
        }
        return isSequential
    }

    BACKEND_URL = process.env.REACT_APP_API_BASE

    async loadProgram(programId) {
        this.globalHwm = 0
        this.globalLastNext = 0
        this.globalHwmJustEarned = false

        if (USE_NEW_API && !this.staging) {
            this.program = await getProgramFull(programId)
        } else {
            const url = this.staging
                ? `${this.BACKEND_URL}/api/program/staging-preview/${programId}`
                : `${this.BACKEND_URL}/api/program/${programId}`
            const res = await fetch(
                url,
                this.staging ? { credentials: 'include' } : undefined
            )
            this.program = await res.json()
        }

        this.loadProgress()

        // validate indices
        if (this.subIndex >= this.program.subprograms.length) {
            this.subIndex = 0
            this.screenIndex = 0
        }

        const currentSub = this.program.subprograms[this.subIndex]

        if (!currentSub || this.screenIndex >= currentSub.screens.length) {
            this.screenIndex = 0
        }

        await this.loadCurrentScreenData()

        this.notify()
    }

    getCurrentScreen() {
        if (!this.program) return null
        const sub = this.program.subprograms[this.subIndex]

        if (!sub) return null
        const screen = sub.screens[this.screenIndex]

        if (!screen) return null
        return screen
    }

    getCurrentSubprogram() {
        if (!this.program) return null
        return this.program.subprograms[this.subIndex]
    }

    // ── Screen visibility ────────────────────────────────────────────────
    // Screens flagged `hidden` in the admin are skipped for the end user
    // (navigation + timeline) but stay in the array so indices remain stable
    // and other screens can still reference them by id (e.g. Play fetching an
    // Assembly screen). Screens without the field default to visible.
    isScreenVisible(screen) {
        return !!screen && !screen.hidden
    }

    // First visible screen index scanning from `startIdx` in direction `dir`
    // (+1 forward, -1 backward). Returns -1 when none found.
    _firstVisibleFrom(startIdx, dir = 1) {
        const sub = this.getCurrentSubprogram()
        if (!sub || !Array.isArray(sub.screens)) return -1
        for (
            let i = startIdx;
            i >= 0 && i < sub.screens.length;
            i += dir
        ) {
            if (this.isScreenVisible(sub.screens[i])) return i
        }
        return -1
    }

    // If the current index landed on a hidden screen (initial load, restored
    // progress, or a direct goTo), nudge it to the nearest visible screen.
    _ensureVisibleScreen() {
        const sub = this.getCurrentSubprogram()
        if (!sub || !Array.isArray(sub.screens) || sub.screens.length === 0)
            return
        if (this.isScreenVisible(sub.screens[this.screenIndex])) return
        let idx = this._firstVisibleFrom(this.screenIndex, 1)
        if (idx === -1) idx = this._firstVisibleFrom(this.screenIndex, -1)
        if (idx !== -1) this.screenIndex = idx
    }

    async loadCurrentScreenData() {
        this._ensureVisibleScreen()
        const screen = this.getCurrentScreen()
        if (!screen) return

        // NEW API (live only): the screen already carries its runtime content
        // (pre-resolved during loadProgram) — no fetch needed.
        if (USE_NEW_API && !this.staging) {
            this.currentScreenData = screen.__runtimeData || null
            return
        }

        const projectId = screen.projectId || this.getCurrentSubprogram()?.id

        // Build URL: include screenId when available (new multi-screen format).
        // In staging mode read the unpublished screen from the admin endpoint.
        const apiBase = this.staging
            ? `${this.BACKEND_URL}/api/staging/${screen.type}/${projectId}`
            : `${this.BACKEND_URL}/api/${screen.type}/${projectId}`
        let url = apiBase
        if (screen.id) {
            url += `/${screen.id}`
        }

        try {
            const res = await fetch(
                url,
                this.staging ? { credentials: 'include' } : undefined
            )
            if (!res.ok) throw new Error('Failed to fetch screen')
            this.currentScreenData = await res.json()
        } catch (err) {
            console.error('Screen load error:', err)
            this.currentScreenData = null
        }
    }

    async next() {
        if (!this.program) return
        const currentSub = this.program.subprograms[this.subIndex]

        // If the last step pressed was NOT just earned sequentially, break the
        // chain so the next screen cannot accidentally continue earning from
        // a globalLastNext that was set by pressing Next on an already-earned step.
        if (!this.globalHwmJustEarned) {
            this.breakSequentialChain()
        }
        this.globalHwmJustEarned = false  // reset for next screen

        // Advance to the next VISIBLE screen, skipping any hidden ones.
        const nextIdx = this._firstVisibleFrom(this.screenIndex + 1, 1)
        if (nextIdx !== -1) {
            // Move to next screen within the SAME subprogram
            this.screenIndex = nextIdx
            this.slideDirection = 'left'

            // --- FIX: Force the next screen to start at step 1 ---
            this._pendingStep = 1
        } else {
            // Last screen of this subprogram — fire completion callback
            console.log('Subprogram complete:', currentSub.id)
            if (this.onSubprogramComplete) {
                this.onSubprogramComplete()
                return
            }
            // No callback set — just log
            console.log('No onSubprogramComplete callback set')
            return
        }

        this.saveProgress()
        await this.loadCurrentScreenData()
        this.notify()
    }

    // Navigate to the previous screen, landing on its LAST step.
    // Used by the Previous button so the user can continue stepping backward.
    async prev() {
        if (!this.program) return
        const prevIdx = this._firstVisibleFrom(this.screenIndex - 1, -1)
        if (prevIdx !== -1) {
            // Use _resolveStepCount so we fall back to the session-cached count
            // when the backend stepCount is missing (0/undefined). Without this,
            // prevScreenTotalSteps is always 1 and the user lands at step 1
            // instead of the last step of the previous screen.
            const prevScreenTotalSteps = this._resolveStepCount(prevIdx) || 1

            this.screenIndex = prevIdx
            this.slideDirection = 'right'
            this._pendingStep = prevScreenTotalSteps
        } else {
            if (typeof this.onExitRequest === 'function') {
                this.onExitRequest()
            } else {
                history.back()
            }
            return
        }

        this.saveProgress()
        await this.loadCurrentScreenData()
        this.notify()
    }

    // Navigate to the previous screen, landing on its FIRST step.
    // Used by the top-left back button to return to the screen's starting page.
    async prevToStart() {
        if (!this.program) return
        const prevIdx = this._firstVisibleFrom(this.screenIndex - 1, -1)
        if (prevIdx !== -1) {
            this.screenIndex = prevIdx
            this.slideDirection = 'right'
            this._pendingStep = 1
        } else {
            if (typeof this.onExitRequest === 'function') {
                this.onExitRequest()
            } else {
                history.back()
            }
            return
        }

        this.saveProgress()
        await this.loadCurrentScreenData()
        this.notify()
    }

    async goTo(subIdx, screenIdx) {
        if (!this.program) return
        if (subIdx < 0 || subIdx >= this.program.subprograms.length) return
        const sub = this.program.subprograms[subIdx]
        if (screenIdx < 0 || screenIdx >= sub.screens.length) return

        this.subIndex = subIdx
        this.screenIndex = screenIdx

        this.saveProgress()
        await this.loadCurrentScreenData()
        this.notify()
    }

    subscribe(fn) {
        this.listeners.push(fn)

        return () => {
            this.listeners = this.listeners.filter((l) => l !== fn)
        }
    }

    notify() {
        this.listeners.forEach((fn) => fn())
    }

    saveProgress() {
        const progress = {
            subIndex: this.subIndex,
            screenIndex: this.screenIndex,
        }

        sessionStorage.setItem('courseProgress', JSON.stringify(progress))
    }

    getProgress() {
        return `${this.subIndex}_${this.screenIndex}`
    }

    loadProgress() {
        const saved = sessionStorage.getItem('courseProgress')

        if (!saved) {
            this.subIndex = 0
            this.screenIndex = 0
            return
        }

        try {
            const progress = JSON.parse(saved)

            this.subIndex = progress.subIndex ?? 0
            this.screenIndex = progress.screenIndex ?? 0
        } catch {
            this.subIndex = 0
            this.screenIndex = 0
        }
    }

    // ── Step count tracking for continuous numbering across screens ──

    /**
     * Register the total step count for a screen.
     * Now a no-op — step counts come from the backend payload.
     * Kept for backward-compatibility so existing screen components
     * don't throw if they still call it during migration.
     */
    registerScreenStepCount(_subIdx, _screenIdx, _count) {
        // no-op: step counts are provided by the backend in program.subprograms[].screens[].stepCount
    }

    /**
     * Resolve the step count for a given screen index.
     * Prefers the backend-provided stepCount, falls back to the runtime cache
     * written by ProgramTimelineNav (key: stepCount_<subIndex>_<screenIndex>).
     * This prevents offset miscalculation when stepCount is missing in program
     * metadata, which would otherwise collapse all screens to offset 0 and cause
     * cross-screen timeline highlight bleed.
     */
    _resolveStepCount(screenIdx) {
        const sub = this.getCurrentSubprogram()
        if (!sub) return 0
        const backendCount = sub.screens[screenIdx]?.stepCount || 0
        if (backendCount > 0) return backendCount
        const cached = sessionStorage.getItem(`stepCount_${this.subIndex}_${screenIdx}`)
        return cached ? parseInt(cached, 10) : 0
    }

    /**
     * Get the cumulative step offset for the current screen.
     * Returns the sum of stepCount from all previous screens
     * in the same subprogram, using backend-provided data with session cache fallback.
     */
    getStepOffset() {
        const sub = this.getCurrentSubprogram()
        if (!sub) return 0
        let offset = 0
        for (let i = 0; i < this.screenIndex; i++) {
            // Hidden screens are invisible to the learner, so their steps must
            // not count toward the visible global step numbering.
            if (sub.screens[i]?.hidden) continue
            offset += this._resolveStepCount(i)
        }
        return offset
    }

    /**
     * Get step info for ALL screens in the current subprogram.
     * Returns array of { screenIndex, screenType, stepCount, offset }.
     * Uses backend-provided stepCount with session cache fallback.
     */
    getAllScreenSteps() {
        const sub = this.getCurrentSubprogram()
        if (!sub) return []
        const result = []
        let cumOffset = 0
        for (let i = 0; i < sub.screens.length; i++) {
            // Omit hidden screens from the timeline entirely. The real array
            // index is preserved on each entry so goToScreen still targets the
            // correct screen.
            if (sub.screens[i]?.hidden) continue
            const count = this._resolveStepCount(i)
            result.push({
                screenIndex: i,
                screenType: sub.screens[i].type,
                stepCount: count,
                offset: cumOffset,
            })
            cumOffset += count
        }
        return result
    }

    /**
     * Navigate to a specific screen and step within the current subprogram.
     * stepInScreen is 1-based local step number.
     */
    // Break the sequential earning chain. Called by any circle-click navigation
    // (same-screen or cross-screen) so that the user cannot accidentally earn
    // the next step just by pressing Next from an already-earned position they
    // jumped back to. Sets globalLastNext to -1, which can never equal globalHwm
    // (≥ 0), so the earning condition fails until the chain is rebuilt by pressing
    // Next sequentially from globalHwm onward.
    // Resets to 0 (not -1) so that global step 1 remains earnable after any
    // circle click — there is no "step 0" to press Next from, so step 1 is
    // always the universal re-entry point for the sequential chain.
    // For steps N>1: globalLastNext(0) ≠ globalHwm(N), so earning is still
    // blocked until the user presses Next from step globalHwm to restore it.
    breakSequentialChain() {
        this.globalLastNext = 0
        this.globalHwmJustEarned = false
    }

    async goToScreen(screenIdx, stepInScreen) {
        if (!this.program) return
        const sub = this.getCurrentSubprogram()
        if (!sub || screenIdx < 0 || screenIdx >= sub.screens.length) return

        // Any circle-click navigation breaks the sequential chain
        this.breakSequentialChain()

        if (screenIdx !== this.screenIndex) {
            this.slideDirection =
                screenIdx > this.screenIndex ? 'left' : 'right'
            this.screenIndex = screenIdx
            this.saveProgress()
            await this.loadCurrentScreenData()
        }

        // Store the target step so the screen component can pick it up
        this._pendingStep = stepInScreen
        this.notify()
    }
}

export default new ContentEngine()
